import { createBrowserRouter, redirect } from 'react-router-dom'
import { Layout } from './Layout'
import { Suspense } from 'react'
import { HELP_BASE } from '@/features/help/_shared/paths'
import { UserLayout } from '@/features/user/layout'
import { RouteErrorBoundary } from './RouteErrorBoundary'
import { NotFound } from './NotFound'
import { LoginPlaceholder } from './LoginPlaceholder'
import { AdminRoute } from '@/features/admin/_shared/AdminRoute'
import { LineupStepRedirect } from './LineupStepRedirect'
import { RootRedirect } from './RootRedirect'
import { lazyRoute } from './lazyRoute'

const DashboardPage = lazyRoute(() => import('@/features/user/dashboard/page'))
const RecordsPage = lazyRoute(() => import('@/features/user/records/page'))
const TimelinePage = lazyRoute(() => import('@/features/user/timeline/page'))
const TeamRequestsPage = lazyRoute(() => import('@/features/user/team-requests/page'))
const SettingsPage = lazyRoute(() => import('@/features/user/settings/page'))
const SettingsRedirect = lazyRoute(() => import('@/features/user/settings/redirect'))
const ReferralPage = lazyRoute(() => import('@/features/user/referral/page'))
const CreateTeamPage = lazyRoute(() => import('@/features/signup/create-team/page'))
const RegisterPage = lazyRoute(() => import('@/features/signup/register/page'))
const SuccessPage = lazyRoute(() => import('@/features/signup/success/page'))
const PrivacyPage = lazyRoute(() => import('@/features/home/privacy/page'))
const SupportUsPage = lazyRoute(() => import('@/features/home/supportus/page'))
const HelpIndexPage = lazyRoute(() => import('@/features/help/page'))
const FreeAgencyHelpPage = lazyRoute(() => import('@/features/help/free-agency-page'))
const StatisticsGlossaryHelpPage = lazyRoute(() => import('@/features/help/statistics-glossary-page'))
const RatingsHelpPage = lazyRoute(() => import('@/features/help/ratings-page'))
const LineupsHelpPage = lazyRoute(() => import('@/features/help/lineups-page'))
const DepthChartsHelpPage = lazyRoute(() => import('@/features/help/depth-charts-page'))
const PitchingRolesHelpPage = lazyRoute(() => import('@/features/help/pitching-roles-page'))
const StadiumsHelpPage = lazyRoute(() => import('@/features/help/stadiums-page'))
const ManagerSettingsHelpPage = lazyRoute(() => import('@/features/help/manager-settings-page'))
const PositionChangesHelpPage = lazyRoute(() => import('@/features/help/position-changes-page'))
// Reachable by URL and from the in-app notes, but deliberately absent from the help
// catalog until the position-system flag goes wide -- see topics/multi-position.tsx.
const MultiPositionHelpPage = lazyRoute(() => import('@/features/help/multi-position-page'))
const TradesHelpPage = lazyRoute(() => import('@/features/help/trades-page'))
const AmateurDraftHelpPage = lazyRoute(() => import('@/features/help/amateur-draft-page'))
const DraftLotteryHelpPage = lazyRoute(() => import('@/features/help/draft-lottery-page'))
const PlayerDevelopmentHelpPage = lazyRoute(() => import('@/features/help/player-development-page'))
const ScoutingHelpPage = lazyRoute(() => import('@/features/help/scouting-page'))
const FatigueHelpPage = lazyRoute(() => import('@/features/help/fatigue-page'))
const PlayerRetirementHelpPage = lazyRoute(() => import('@/features/help/player-retirement-page'))
const PlayerCardDataHelpPage = lazyRoute(() => import('@/features/help/player-card-data-page'))
const TeamFinancesHelpPage = lazyRoute(() => import('@/features/help/team-finances-page'))
const BullpenLogicHelpPage = lazyRoute(() => import('@/features/help/bullpen-logic-page'))
const TeamPageNavigationHelpPage = lazyRoute(() => import('@/features/help/team-page-navigation-page'))
const RostersHelpPage = lazyRoute(() => import('@/features/help/rosters-page'))
const LeaguePageNavigationHelpPage = lazyRoute(() => import('@/features/help/league-page-navigation-page'))
const SimScheduleHelpPage = lazyRoute(() => import('@/features/help/sim-schedule-page'))
const SeasonStructureHelpPage = lazyRoute(() => import('@/features/help/season-structure-page'))
const SimModelsHelpPage = lazyRoute(() => import('@/features/help/sim-models-page'))
const LeagueSettingsHelpPage = lazyRoute(() => import('@/features/help/league-settings-page'))
const PlayoffsHelpPage = lazyRoute(() => import('@/features/help/playoffs-page'))
const HallOfFameHelpPage = lazyRoute(() => import('@/features/help/hall-of-fame-page'))
const JoiningALeagueHelpPage = lazyRoute(() => import('@/features/help/joining-a-league-page'))
const ReleaseNotesHelpPage = lazyRoute(() => import('@/features/help/release-notes-page'))
const ReleaseNoteHelpPage = lazyRoute(() => import('@/features/help/release-note-page'))
const BlogPage = lazyRoute(() => import('@/features/blog/page'))
const BlogPostPage = lazyRoute(() => import('@/features/blog/post-page'))
const BlogManagePage = lazyRoute(() => import('@/features/blog/manage/page'))
const BlogEditorPage = lazyRoute(() => import('@/features/blog/manage/editor-page'))
const WhatsNewHelpPage = lazyRoute(() => import('@/features/help/whats-new-page'))
const WhatsNewTldrHelpPage = lazyRoute(() => import('@/features/help/whats-new-tldr-page'))
const ThingsYouMissedHelpPage = lazyRoute(() => import('@/features/help/things-you-missed-page'))
const CommissionerInfoHelpPage = lazyRoute(() => import('@/features/help/commissioner-info-page'))
const ForumsHelpPage = lazyRoute(() => import('@/features/help/forums-page'))
const GoogleSheetsHelpPage = lazyRoute(() => import('@/features/help/google-sheets-page'))
const StrategyGuidesHelpPage = lazyRoute(() => import('@/features/help/strategy-guides-page'))
const SalaryCapSurvivorHelpPage = lazyRoute(() => import('@/features/help/salary-cap-survivor-page'))
const FaqHelpPage = lazyRoute(() => import('@/features/help/faq-page'))
const AcceptableUsePolicyHelpPage = lazyRoute(() => import('@/features/help/acceptable-use-policy-page'))
const TeamOwnershipHelpPage = lazyRoute(() => import('@/features/help/team-ownership-page'))
const LeagueActivityHelpPage = lazyRoute(() => import('@/features/help/league-activity-page'))
const PlayerAchievementsHelpPage = lazyRoute(() => import('@/features/help/player-achievements-page'))
const StoryOfWinthropHelpPage = lazyRoute(() => import('@/features/help/story-of-winthrop-page'))
const GettingStartedHelpPage = lazyRoute(() => import('@/features/help/getting-started-page'))
const UnderstandingYourPlayersHelpPage = lazyRoute(() => import('@/features/help/understanding-your-players-page'))
const SettingYourLineupHelpPage = lazyRoute(() => import('@/features/help/setting-your-lineup-page'))
const HowGamesWorkHelpPage = lazyRoute(() => import('@/features/help/how-games-work-page'))
const BuildingYourTeamHelpPage = lazyRoute(() => import('@/features/help/building-your-team-page'))
const TeamFreeAgentsPage = lazyRoute(() => import('@/features/team/freeagents/page'))
const StandingsPage = lazyRoute(() => import('@/features/league/standings/page'))
const SeasonsPage = lazyRoute(() => import('@/features/league/seasons/page'))
const StadiumsPage = lazyRoute(() => import('@/features/league/stadiums/page'))
const FinancialsPage = lazyRoute(() => import('@/features/league/financials/page'))
const TransactionsPage = lazyRoute(() => import('@/features/league/transactions/page'))
const TradesPage = lazyRoute(() => import('@/features/league/trades/page'))
const LeagueTradeDetailPage = lazyRoute(() => import('@/features/league/trade/detail-page'))
const AwardsPage = lazyRoute(() => import('@/features/league/awards/page'))
const StatsPage = lazyRoute(() => import('@/features/league/stats/page'))
const TeamStatsPage = lazyRoute(() => import('@/features/league/teamstats/page'))
const SeasonLeadersPage = lazyRoute(() => import('@/features/league/seasonleaders/page'))
const AllTimeLeadersPage = lazyRoute(() => import('@/features/league/alltimeleaders/page'))
const ScoreboardPage = lazyRoute(() => import('@/features/league/scoreboard/page'))
const PlayersPage = lazyRoute(() => import('@/features/league/players/page'))
const HallOfFamePage = lazyRoute(() => import('@/features/league/halloffame/page'))
const AchievementsPage = lazyRoute(() => import('@/features/league/achievements/page'))
const AchievementListPage = lazyRoute(() => import('@/features/league/achievements/list-page'))
const DraftPage = lazyRoute(() => import('@/features/league/draft/page'))
const DraftLotteryPage = lazyRoute(() => import('@/features/league/draftlottery/page'))
const PlayoffsPage = lazyRoute(() => import('@/features/league/playoffs/page'))
const LeagueViewPage = lazyRoute(() => import('@/features/league/view'))
const CommishToolsPage = lazyRoute(() => import('@/features/league/commishtools'))
const LeagueCommishNotificationsPage = lazyRoute(() => import('@/features/league/commishnotifications'))
const StructurePage = lazyRoute(() => import('@/features/league/structure'))
const TeamOwnershipPage = lazyRoute(() => import('@/features/league/teamownership'))
const LeagueSettingsPage = lazyRoute(() => import('@/features/league/settings'))
const DraftReorderPage = lazyRoute(() => import('@/features/league/draftreorder'))
const LowMinorsCheckPage = lazyRoute(() => import('@/features/league/lowminorscheck'))
const ReassignPage = lazyRoute(() => import('@/features/league/reassign'))
const OfferDrawer = lazyRoute(() => import('@/features/team/freeagents/offer'))
const RosterPage = lazyRoute(() => import('@/features/team/roster/page'))
const TeamSeasonsHistoryPage = lazyRoute(() => import('@/features/team/seasons/page'))
const TeamAchievementsHistoryPage = lazyRoute(() => import('@/features/team/achievements/page'))
const TeamAwardsHistoryPage = lazyRoute(() => import('@/features/team/awards/page'))
const TeamDraftHistoryPage = lazyRoute(() => import('@/features/team/drafthistory/page'))
const TeamLeadersPage = lazyRoute(() => import('@/features/team/leaders/page'))
const TeamTransactionsPage = lazyRoute(() => import('@/features/team/transactions/page'))
const TeamStatsViewPage = lazyRoute(() => import('@/features/team/stats/page'))
const TeamAdvancedDefensePage = lazyRoute(() => import('@/features/team/advanceddefense/page'))
const TeamSchedulePage = lazyRoute(() => import('@/features/team/schedule/page'))
const TeamNewsPage = lazyRoute(() => import('@/features/team/news/page'))
const TeamViewPage = lazyRoute(() => import('@/features/team/view/page'))
const TeamLineupsPage = lazyRoute(() => import('@/features/team/lineups/page'))
const TeamDepthChartPage = lazyRoute(() => import('@/features/team/depthchart/page'))
const TeamDefensePage = lazyRoute(() => import('@/features/team/defense/page'))
const TeamBattingOrderPage = lazyRoute(() => import('@/features/team/battingorder/page'))
const TeamPitchingRolesPage = lazyRoute(() => import('@/features/team/pitchingroles/page'))
const TeamManagerPage = lazyRoute(() => import('@/features/team/manager/page'))
const TeamPositionChangesPage = lazyRoute(() => import('@/features/team/positionchanges/page'))
const TeamDraftListPage = lazyRoute(() => import('@/features/team/draftlist/page'))
const TeamTradesPage = lazyRoute(() => import('@/features/team/trades/page'))
const TeamTradeDetailPage = lazyRoute(() => import('@/features/team/trade/detail-page'))
const TeamTradeProposalPage = lazyRoute(() => import('@/features/team/trade/proposal-page'))
const PlayerViewPage = lazyRoute(() => import('@/features/player/view/page'))
const PlayerStatsPage = lazyRoute(() => import('@/features/player/stats/page'))
const PlayerSplitsPage = lazyRoute(() => import('@/features/player/splits/page'))
const PlayerAdvancedDefensePage = lazyRoute(() => import('@/features/player/advanceddefense/page'))
const PlayerGameLogPage = lazyRoute(() => import('@/features/player/gamelog/page'))
const PlayerAchievementsAwardsPage = lazyRoute(() => import('@/features/player/achievementsawards/page'))
const PlayerHistoryPage = lazyRoute(() => import('@/features/player/history/page'))
const GamePage = lazyRoute(() => import('@/features/game/game/page'))
const GameViewPage = lazyRoute(() => import('@/features/game/gameview/page'))
const FinderPage = lazyRoute(() => import('@/features/finder/FinderPage'))
const FindManagerPage = lazyRoute(() => import('@/features/user/search/page'))
const CreateTeamAdditionalPage = lazyRoute(() => import('@/features/teams/create/page'))
const AdminAnnouncementsPage = lazyRoute(() => import('@/features/admin/announcements/page'))
const AdminReleaseNotesPage = lazyRoute(() => import('@/features/admin/release-notes/page'))
const AdminReleaseNoteEditorPage = lazyRoute(() => import('@/features/admin/release-notes/editor-page'))
const AdminBlogAuthorsPage = lazyRoute(() => import('@/features/admin/blog-authors/page'))
const AdminHubPage = lazyRoute(() => import('@/features/admin/hub/page'))
const AdminUserLookupPage = lazyRoute(() => import('@/features/admin/user-lookup/page'))
const AdminReverseTradePage = lazyRoute(() => import('@/features/admin/reverse-trade/page'))
const AdminRetirePlayerPage = lazyRoute(() => import('@/features/admin/retire-player/page'))
const AdminBanUnbanUserPage = lazyRoute(() => import('@/features/admin/ban-unban-user/page'))
const AdminSwapTeamDivisionsPage = lazyRoute(() => import('@/features/admin/swap-team-divisions/page'))
const AdminRenameTeamPage = lazyRoute(() => import('@/features/admin/rename-team/page'))
const AdminCreateFreeAgentsPage = lazyRoute(() => import('@/features/admin/create-free-agents/page'))
const AdminMessageReportsPage = lazyRoute(() => import('@/features/admin/message-reports/page'))
const AdminSimHealthPage = lazyRoute(() => import('@/features/admin/sim-health/page'))
const AdminEmailDeliveryPage = lazyRoute(() => import('@/features/admin/email-delivery/page'))
const AdminFeatureFlagsPage = lazyRoute(() => import('@/features/admin/feature-flags/page'))
const AdminCreateLeaguePage = lazyRoute(() => import('@/features/admin/create-league/page'))
const MessagesPage = lazyRoute(() => import('@/features/messages/page'))
const NotificationsPage = lazyRoute(() => import('@/features/notifications/page'))
const NotificationPreferencesPage = lazyRoute(() => import('@/features/notifications/preferences/page'))
const NotificationPreferencesRedirect = lazyRoute(() => import('@/features/notifications/preferences/redirect'))

const pageFallback = <div className="text-center py-8 text-gray-400">Loading...</div>

// Visual gallery for dashboard alert states (see __preview__/alert-gallery).
// Enabled in local dev AND on the TEST build (VITE_ENABLE_DEV_GALLERY, set only
// in .env.test) so it can be shared with the team. The flag is absent from
// .env.production, so the dynamic import below is dead code in a prod build and
// Rollup eliminates it (chunk and all) — the gallery never reaches prod.
//
// Path lives UNDER /leagues on purpose: the `leagues.react` IIS rewrite in
// Web.config matches the `^leagues(/|$)` prefix, so /leagues/* already serves
// the React app's index.html. A `/dev/*` URL has no rewrite rule and 404s at
// IIS before React loads. Hosting here keeps this a React-only change (no
// Web.config edit, deployable via the React-only script).
const galleryEnabled = import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEV_GALLERY === 'true'
const devRoutes = galleryEnabled
  ? (() => {
      const DraftWatchlistMockup = lazyRoute(() => import('@/features/team/draftlist/__preview__/draft-watchlist-mockup'))
      const SandboxFlowMockup = lazyRoute(() => import('@/features/league/view/__preview__/sandbox-flow-mockup'))
      const AutofillProbe = lazyRoute(() => import('@/features/_dev/autofill-probe'))
      const DateConventionMockup = lazyRoute(() => import('@/features/_shared/__preview__/date-convention-mockup'))
      return [
        { path: '/leagues/date-convention-mockup', element: <Suspense fallback={pageFallback}><DateConventionMockup /></Suspense> },
        { path: '/leagues/sandbox-flow-mockup', element: <Suspense fallback={pageFallback}><SandboxFlowMockup /></Suspense> },
        { path: '/leagues/draft-watchlist-mockup', element: <Suspense fallback={pageFallback}><DraftWatchlistMockup /></Suspense> },
        { path: '/leagues/autofill-probe', element: <Suspense fallback={pageFallback}><AutofillProbe /></Suspense> },
      ]
    })()
  : []

export const router = createBrowserRouter([
  {
    element: <Layout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // Root '/' — the consolidated app now owns it (was the legacy index.asp
      // home). Send a logged-in user to their dashboard; RequireAuth gates the
      // logged-out case to login.
      { index: true, element: <RootRedirect /> },
      // Static /user/referral and /user/settings must appear before the dynamic
      // /user/:userId group so React Router's static-segment-wins rule is explicit
      // in the route tree. /user/settings is a resolver that forwards to
      // /user/{id}/settings, preserving query params (e.g. ?emailVerificationToken=).
      {
        path: '/user/referral',
        element: <Suspense fallback={pageFallback}><ReferralPage /></Suspense>,
      },
      {
        path: '/user/settings',
        element: <Suspense fallback={pageFallback}><SettingsRedirect /></Suspense>,
      },
      {
        path: '/user/:userId',
        element: <UserLayout />,
        children: [
          { index: true, element: <Suspense fallback={pageFallback}><DashboardPage /></Suspense> },
          { path: 'records', element: <Suspense fallback={pageFallback}><RecordsPage /></Suspense> },
          { path: 'timeline', element: <Suspense fallback={pageFallback}><TimelinePage /></Suspense> },
          { path: 'teamrequests', element: <Suspense fallback={pageFallback}><TeamRequestsPage /></Suspense> },
          { path: 'settings', element: <Suspense fallback={pageFallback}><SettingsPage /></Suspense> },
          { path: 'settings/notifications', element: <Suspense fallback={pageFallback}><NotificationPreferencesPage /></Suspense> },
        ],
      },
      {
        path: '/user/password/reset',
        element: <Suspense fallback={pageFallback}><SettingsPage /></Suspense>,
      },
      // LoginModal opens itself on /login and /login/*; this keeps NotFound
      // from rendering (and logging a PageNotFound) underneath it.
      { path: '/login/*', element: <LoginPlaceholder /> },
      {
        path: '/privacy',
        element: <Suspense fallback={pageFallback}><PrivacyPage /></Suspense>,
      },
      {
        path: '/supportus',
        element: <Suspense fallback={pageFallback}><SupportUsPage /></Suspense>,
      },
      // Members-only in-app blog (deny-by-default on the server — no client-side
      // auth guard needed). /blog/manage, /blog/manage/new and /blog/manage/:blogPostId
      // (the authoring list + editor) are declared BEFORE the dynamic /blog/:blogPostId
      // article route below, or that route would swallow /blog/manage as a post id.
      {
        path: '/blog',
        element: <Suspense fallback={pageFallback}><BlogPage /></Suspense>,
      },
      {
        path: '/blog/manage',
        element: <Suspense fallback={pageFallback}><BlogManagePage /></Suspense>,
      },
      {
        path: '/blog/manage/new',
        element: <Suspense fallback={pageFallback}><BlogEditorPage /></Suspense>,
      },
      {
        path: '/blog/manage/:blogPostId',
        element: <Suspense fallback={pageFallback}><BlogEditorPage /></Suspense>,
      },
      {
        path: '/blog/:blogPostId',
        element: <Suspense fallback={pageFallback}><BlogPostPage /></Suspense>,
      },
      {
        path: HELP_BASE,
        element: <Suspense fallback={pageFallback}><HelpIndexPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/whats-new`,
        element: <Suspense fallback={pageFallback}><WhatsNewHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/whats-new-tldr`,
        element: <Suspense fallback={pageFallback}><WhatsNewTldrHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/things-you-missed`,
        element: <Suspense fallback={pageFallback}><ThingsYouMissedHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/free-agency`,
        element: <Suspense fallback={pageFallback}><FreeAgencyHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/statistics-glossary`,
        element: <Suspense fallback={pageFallback}><StatisticsGlossaryHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/ratings`,
        element: <Suspense fallback={pageFallback}><RatingsHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/lineups`,
        element: <Suspense fallback={pageFallback}><LineupsHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/depth-charts`,
        element: <Suspense fallback={pageFallback}><DepthChartsHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/pitching-roles`,
        element: <Suspense fallback={pageFallback}><PitchingRolesHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/stadiums`,
        element: <Suspense fallback={pageFallback}><StadiumsHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/manager-settings`,
        element: <Suspense fallback={pageFallback}><ManagerSettingsHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/position-changes`,
        element: <Suspense fallback={pageFallback}><PositionChangesHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/multi-position`,
        element: <Suspense fallback={pageFallback}><MultiPositionHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/trades`,
        element: <Suspense fallback={pageFallback}><TradesHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/amateur-draft`,
        element: <Suspense fallback={pageFallback}><AmateurDraftHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/draft-lottery`,
        element: <Suspense fallback={pageFallback}><DraftLotteryHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/player-development`,
        element: <Suspense fallback={pageFallback}><PlayerDevelopmentHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/scouting`,
        element: <Suspense fallback={pageFallback}><ScoutingHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/fatigue`,
        element: <Suspense fallback={pageFallback}><FatigueHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/player-retirement`,
        element: <Suspense fallback={pageFallback}><PlayerRetirementHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/player-card-data`,
        element: <Suspense fallback={pageFallback}><PlayerCardDataHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/team-finances`,
        element: <Suspense fallback={pageFallback}><TeamFinancesHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/bullpen-logic`,
        element: <Suspense fallback={pageFallback}><BullpenLogicHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/team-page-navigation`,
        element: <Suspense fallback={pageFallback}><TeamPageNavigationHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/rosters`,
        element: <Suspense fallback={pageFallback}><RostersHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/league-page-navigation`,
        element: <Suspense fallback={pageFallback}><LeaguePageNavigationHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/sim-schedule`,
        element: <Suspense fallback={pageFallback}><SimScheduleHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/season-structure`,
        element: <Suspense fallback={pageFallback}><SeasonStructureHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/sim-models`,
        element: <Suspense fallback={pageFallback}><SimModelsHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/league-settings`,
        element: <Suspense fallback={pageFallback}><LeagueSettingsHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/playoffs`,
        element: <Suspense fallback={pageFallback}><PlayoffsHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/hall-of-fame`,
        element: <Suspense fallback={pageFallback}><HallOfFameHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/joining-a-league`,
        element: <Suspense fallback={pageFallback}><JoiningALeagueHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/release-notes`,
        element: <Suspense fallback={pageFallback}><ReleaseNotesHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/release-notes/:releaseNoteId`,
        element: <Suspense fallback={pageFallback}><ReleaseNoteHelpPage /></Suspense>,
      },
      // The old "Recent Updates" stub was replaced by Release Notes; keep its links working.
      {
        path: `${HELP_BASE}/recent-updates`,
        loader: () => redirect(`${HELP_BASE}/release-notes`),
      },
      {
        path: `${HELP_BASE}/commissioner-info`,
        element: <Suspense fallback={pageFallback}><CommissionerInfoHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/forums`,
        element: <Suspense fallback={pageFallback}><ForumsHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/google-sheets`,
        element: <Suspense fallback={pageFallback}><GoogleSheetsHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/strategy-guides`,
        element: <Suspense fallback={pageFallback}><StrategyGuidesHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/salary-cap-survivor`,
        element: <Suspense fallback={pageFallback}><SalaryCapSurvivorHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/faq`,
        element: <Suspense fallback={pageFallback}><FaqHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/acceptable-use-policy`,
        element: <Suspense fallback={pageFallback}><AcceptableUsePolicyHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/team-ownership`,
        element: <Suspense fallback={pageFallback}><TeamOwnershipHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/league-activity`,
        element: <Suspense fallback={pageFallback}><LeagueActivityHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/player-achievements`,
        element: <Suspense fallback={pageFallback}><PlayerAchievementsHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/story-of-winthrop`,
        element: <Suspense fallback={pageFallback}><StoryOfWinthropHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/getting-started`,
        element: <Suspense fallback={pageFallback}><GettingStartedHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/understanding-your-players`,
        element: <Suspense fallback={pageFallback}><UnderstandingYourPlayersHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/setting-your-lineup`,
        element: <Suspense fallback={pageFallback}><SettingYourLineupHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/how-games-work`,
        element: <Suspense fallback={pageFallback}><HowGamesWorkHelpPage /></Suspense>,
      },
      {
        path: `${HELP_BASE}/building-your-team`,
        element: <Suspense fallback={pageFallback}><BuildingYourTeamHelpPage /></Suspense>,
      },
      // Legacy /help2 links (shared while the manual was built out at /help2, before
      // the backend /help wiki redirect was dropped) → their new /help home. Path-
      // matched, so placement here doesn't affect matching; search string preserved.
      {
        path: '/help2',
        loader: () => redirect('/help'),
      },
      {
        path: '/help2/*',
        loader: ({ params, request }) =>
          redirect(`/help/${params['*'] ?? ''}${new URL(request.url).search}`),
      },
      {
        path: '/signup',
        element: <Suspense fallback={pageFallback}><CreateTeamPage /></Suspense>,
      },
      {
        path: '/signup/register',
        element: <Suspense fallback={pageFallback}><RegisterPage /></Suspense>,
      },
      {
        path: '/signup/success',
        element: <Suspense fallback={pageFallback}><SuccessPage /></Suspense>,
      },
      {
        path: '/team/:teamId/roster',
        element: <Suspense fallback={pageFallback}><RosterPage /></Suspense>,
      },
      {
        path: '/team/:teamId/seasons',
        element: <Suspense fallback={pageFallback}><TeamSeasonsHistoryPage /></Suspense>,
      },
      {
        path: '/team/:teamId/achievements',
        element: <Suspense fallback={pageFallback}><TeamAchievementsHistoryPage /></Suspense>,
      },
      {
        path: '/team/:teamId/awards',
        element: <Suspense fallback={pageFallback}><TeamAwardsHistoryPage /></Suspense>,
      },
      {
        path: '/team/:teamId/drafthistory',
        element: <Suspense fallback={pageFallback}><TeamDraftHistoryPage /></Suspense>,
      },
      {
        path: '/team/:teamId/leaders',
        element: <Suspense fallback={pageFallback}><TeamLeadersPage /></Suspense>,
      },
      {
        path: '/team/:teamId/transactions',
        element: <Suspense fallback={pageFallback}><TeamTransactionsPage /></Suspense>,
      },
      {
        path: '/team/:teamId/stats',
        element: <Suspense fallback={pageFallback}><TeamStatsViewPage /></Suspense>,
      },
      {
        path: '/team/:teamId/advanceddefense',
        element: <Suspense fallback={pageFallback}><TeamAdvancedDefensePage /></Suspense>,
      },
      {
        path: '/team/:teamId/schedule',
        element: <Suspense fallback={pageFallback}><TeamSchedulePage /></Suspense>,
      },
      {
        path: '/team/:teamId/news',
        element: <Suspense fallback={pageFallback}><TeamNewsPage /></Suspense>,
      },
      {
        path: '/team/:teamId/view',
        element: <Suspense fallback={pageFallback}><TeamViewPage /></Suspense>,
      },
      {
        path: '/team/:teamId/lineups',
        element: <Suspense fallback={pageFallback}><TeamLineupsPage /></Suspense>,
      },
      // Lineup-editor steps, nested under /lineups/* (#361 — URL now matches the
      // "Lineups" section label). The flat routes below 301-mirror to these.
      {
        path: '/team/:teamId/lineups/depthchart',
        element: <Suspense fallback={pageFallback}><TeamDepthChartPage /></Suspense>,
      },
      {
        path: '/team/:teamId/lineups/defense',
        element: <Suspense fallback={pageFallback}><TeamDefensePage /></Suspense>,
      },
      {
        path: '/team/:teamId/lineups/battingorder',
        element: <Suspense fallback={pageFallback}><TeamBattingOrderPage /></Suspense>,
      },
      {
        path: '/team/:teamId/lineups/pitchingroles',
        element: <Suspense fallback={pageFallback}><TeamPitchingRolesPage /></Suspense>,
      },
      // Legacy flat step URLs → client-side redirect to the nested ones (mirrors
      // the IIS 301s; preserves ?vs=). Permanent belt-and-braces for bookmarks.
      { path: '/team/:teamId/depthchart', element: <LineupStepRedirect step="depthchart" /> },
      { path: '/team/:teamId/defense', element: <LineupStepRedirect step="defense" /> },
      { path: '/team/:teamId/battingorder', element: <LineupStepRedirect step="battingorder" /> },
      { path: '/team/:teamId/pitchingroles', element: <LineupStepRedirect step="pitchingroles" /> },
      {
        path: '/team/:teamId/manager',
        element: <Suspense fallback={pageFallback}><TeamManagerPage /></Suspense>,
      },
      {
        path: '/team/:teamId/positionchanges',
        element: <Suspense fallback={pageFallback}><TeamPositionChangesPage /></Suspense>,
      },
      {
        path: '/team/:teamId/draftlist',
        element: <Suspense fallback={pageFallback}><TeamDraftListPage /></Suspense>,
      },
      {
        path: '/team/:teamId/freeagents',
        element: <Suspense fallback={pageFallback}><TeamFreeAgentsPage /></Suspense>,
        children: [
          {
            path: 'offer/:playerId',
            element: <Suspense fallback={pageFallback}><OfferDrawer /></Suspense>,
          },
        ],
      },
      {
        path: '/team/:teamId/trades',
        element: <Suspense fallback={pageFallback}><TeamTradesPage /></Suspense>,
      },
      {
        path: '/team/:teamId/trade/new',
        element: <Suspense fallback={pageFallback}><TeamTradeProposalPage /></Suspense>,
      },
      {
        path: '/team/:teamId/trade/:tradeId/counter',
        element: <Suspense fallback={pageFallback}><TeamTradeProposalPage /></Suspense>,
      },
      {
        path: '/team/:teamId/trade/:tradeId/edit',
        element: <Suspense fallback={pageFallback}><TeamTradeProposalPage /></Suspense>,
      },
      {
        path: '/team/:teamId/trade/:tradeId',
        element: <Suspense fallback={pageFallback}><TeamTradeDetailPage /></Suspense>,
      },
      {
        path: '/leagues',
        element: <Suspense fallback={pageFallback}><FinderPage /></Suspense>,
      },
      {
        path: '/users',
        element: <Suspense fallback={pageFallback}><FindManagerPage /></Suspense>,
      },
      {
        path: '/teams/create',
        element: <Suspense fallback={pageFallback}><CreateTeamAdditionalPage /></Suspense>,
      },
      {
        path: '/teams',
        element: <Suspense fallback={pageFallback}><FinderPage /></Suspense>,
      },
      {
        path: '/messages',
        element: <Suspense fallback={pageFallback}><MessagesPage /></Suspense>,
      },
      {
        path: '/messages/:conversationId',
        element: <Suspense fallback={pageFallback}><MessagesPage /></Suspense>,
      },
      {
        path: '/notifications',
        element: <Suspense fallback={pageFallback}><NotificationsPage /></Suspense>,
      },
      {
        path: '/settings/notifications',
        element: <Suspense fallback={pageFallback}><NotificationPreferencesRedirect /></Suspense>,
      },
      {
        path: '/game/:gameId/gameview',
        element: <Suspense fallback={pageFallback}><GameViewPage /></Suspense>,
      },
      {
        path: '/game/:gameId/:tab?',
        element: <Suspense fallback={pageFallback}><GamePage /></Suspense>,
      },
      {
        path: '/league/:leagueId/standings',
        element: <Suspense fallback={pageFallback}><StandingsPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/seasons',
        element: <Suspense fallback={pageFallback}><SeasonsPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/stadiums',
        element: <Suspense fallback={pageFallback}><StadiumsPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/financials',
        element: <Suspense fallback={pageFallback}><FinancialsPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/transactions',
        element: <Suspense fallback={pageFallback}><TransactionsPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/trades',
        element: <Suspense fallback={pageFallback}><TradesPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/trade/:tradeId',
        element: <Suspense fallback={pageFallback}><LeagueTradeDetailPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/awards',
        element: <Suspense fallback={pageFallback}><AwardsPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/stats',
        element: <Suspense fallback={pageFallback}><StatsPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/teamstats',
        element: <Suspense fallback={pageFallback}><TeamStatsPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/seasonleaders',
        element: <Suspense fallback={pageFallback}><SeasonLeadersPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/alltimeleaders',
        element: <Suspense fallback={pageFallback}><AllTimeLeadersPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/scoreboard',
        element: <Suspense fallback={pageFallback}><ScoreboardPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/players',
        element: <Suspense fallback={pageFallback}><PlayersPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/halloffame',
        element: <Suspense fallback={pageFallback}><HallOfFamePage /></Suspense>,
      },
      {
        path: '/league/:leagueId/achievements',
        element: <Suspense fallback={pageFallback}><AchievementsPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/achievementlist/:achievementTypeId',
        element: <Suspense fallback={pageFallback}><AchievementListPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/draft',
        element: <Suspense fallback={pageFallback}><DraftPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/draftlottery',
        element: <Suspense fallback={pageFallback}><DraftLotteryPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/playoffs',
        element: <Suspense fallback={pageFallback}><PlayoffsPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/view',
        element: <Suspense fallback={pageFallback}><LeagueViewPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/commishtools',
        element: <Suspense fallback={pageFallback}><CommishToolsPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/commishnotifications',
        element: <Suspense fallback={pageFallback}><LeagueCommishNotificationsPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/structure',
        element: <Suspense fallback={pageFallback}><StructurePage /></Suspense>,
      },
      {
        path: '/league/:leagueId/teamownership',
        element: <Suspense fallback={pageFallback}><TeamOwnershipPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/settings',
        element: <Suspense fallback={pageFallback}><LeagueSettingsPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/draftreorder',
        element: <Suspense fallback={pageFallback}><DraftReorderPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/lowminorscheck',
        element: <Suspense fallback={pageFallback}><LowMinorsCheckPage /></Suspense>,
      },
      {
        path: '/league/:leagueId/reassign/:teamId',
        element: <Suspense fallback={pageFallback}><ReassignPage /></Suspense>,
      },
      {
        path: '/player/:playerId/view',
        element: <Suspense fallback={pageFallback}><PlayerViewPage /></Suspense>,
      },
      {
        path: '/player/:playerId/seasonstats',
        element: <Suspense fallback={pageFallback}><PlayerStatsPage part="regular" /></Suspense>,
      },
      {
        path: '/player/:playerId/playoffstats',
        element: <Suspense fallback={pageFallback}><PlayerStatsPage part="playoffs" /></Suspense>,
      },
      {
        path: '/player/:playerId/splitstats',
        element: <Suspense fallback={pageFallback}><PlayerSplitsPage /></Suspense>,
      },
      {
        path: '/player/:playerId/advanceddefense',
        element: <Suspense fallback={pageFallback}><PlayerAdvancedDefensePage /></Suspense>,
      },
      {
        path: '/player/:playerId/gamelog',
        element: <Suspense fallback={pageFallback}><PlayerGameLogPage /></Suspense>,
      },
      {
        path: '/player/:playerId/gamelog/:year',
        element: <Suspense fallback={pageFallback}><PlayerGameLogPage /></Suspense>,
      },
      {
        path: '/player/:playerId/achievementsawards',
        element: <Suspense fallback={pageFallback}><PlayerAchievementsAwardsPage /></Suspense>,
      },
      {
        path: '/player/:playerId/history',
        element: <Suspense fallback={pageFallback}><PlayerHistoryPage /></Suspense>,
      },
      {
        path: '/admin/announcements',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="admin">
              <AdminAnnouncementsPage />
            </AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/release-notes',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="admin"><AdminReleaseNotesPage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/release-notes/new',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="admin"><AdminReleaseNoteEditorPage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/release-notes/:releaseNoteId',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="admin"><AdminReleaseNoteEditorPage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/blog-authors',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="admin">
              <AdminBlogAuthorsPage />
            </AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="staff"><AdminHubPage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/user-lookup',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="staff"><AdminUserLookupPage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/reverse-trade',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="staff"><AdminReverseTradePage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/retire-player',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="staff"><AdminRetirePlayerPage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/ban-unban-user',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="staff"><AdminBanUnbanUserPage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/swap-team-divisions',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="staff"><AdminSwapTeamDivisionsPage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/rename-team',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="staff"><AdminRenameTeamPage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/create-free-agents',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="staff"><AdminCreateFreeAgentsPage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/message-reports',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="staff"><AdminMessageReportsPage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/sim-health',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="admin"><AdminSimHealthPage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/email-delivery',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="admin"><AdminEmailDeliveryPage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/feature-flags',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="admin"><AdminFeatureFlagsPage /></AdminRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/create-league',
        element: (
          <Suspense fallback={pageFallback}>
            <AdminRoute level="admin"><AdminCreateLeaguePage /></AdminRoute>
          </Suspense>
        ),
      },
      ...devRoutes,
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
])
